import { ActionTitle } from "@/components/actionTitle/ActionTitle";
import { type Opportunity } from "@repo/api";
import { Button, Text, arrowDownToLineIcon } from "@repo/skapa";
import { T, t } from "@repo/translation";
import { PaddedContainer, SectionContainer } from "@repo/ui";
import { hasItems } from "@repo/utils";

import { RECOMMENDED_ACTION_IDS } from "../project/projectAI/utils";
import { FileUpload } from "./FileUpload";
import { DownloadAllModal } from "./fileModal/DownloadAllModal";
import { FileDeleteModal } from "./fileModal/FileDeleteModal";
import { FilesList } from "./filesList/FilesList";
import { useFiles } from "./useFiles";
import { FileLoadingEnum } from "./utils";

interface FilesProps {
  opportunity: Opportunity;
  editable: boolean;
}

export const FilesSection = ({ opportunity, editable }: FilesProps) => {
  const {
    loading,
    loadingFileId,
    fileToBeDeleted,
    setFileToBeDeleted,
    attachments,
    appointmentAttachments,
    homeProjectAttachments,
    totalFiles,
    onDelete,
    onDownload,
    onUpload,
    confirmDownloadAll,
    setConfirmDownloadAll,
    handleDownloadAll,
    handleSharingToHomeProject,
    allowSharingToHomeProject,
  } = useFiles(opportunity);

  const hasDownloadableFiles =
    hasItems(attachments) ||
    (hasItems(appointmentAttachments) &&
      appointmentAttachments.some((att) => att.fileStatus === "APPROVED"));

  const hasAttachments =
    hasItems(attachments) ||
    hasItems(appointmentAttachments) ||
    hasItems(homeProjectAttachments);

  return (
    <PaddedContainer id={RECOMMENDED_ACTION_IDS.FILES_SECTION}>
      <SectionContainer>
        <ActionTitle
          title={`${totalFiles > 1 ? t("label.files") : t("label.file")} (${totalFiles || 0})`}
          action={
            editable && hasDownloadableFiles ? (
              <Button
                size="small"
                iconOnly
                data-testid="download_all"
                ssrIcon={arrowDownToLineIcon}
                loading={loading === FileLoadingEnum.DOWNLOADING_ALL}
                onClick={() => setConfirmDownloadAll(true)}
                title={t("action.download_all_files")}
              />
            ) : undefined
          }
        />
        <Text>
          <T translationKey="prompt.files_info" />
        </Text>

        {editable && (
          <FileUpload
            upload={onUpload}
            accept="image/png, image/jpeg, image/jpg, .pdf, .xls, .xlsx, .txt, .csv, .docx, .dwg, .eml, .msg"
          />
        )}

        {hasAttachments && (
          <FilesList
            attachments={attachments}
            appointmentAttachments={appointmentAttachments}
            homeProjectAttachments={homeProjectAttachments}
            editable={editable}
            loading={loading}
            loadingFileId={loadingFileId}
            onDownload={onDownload}
            onDelete={setFileToBeDeleted}
            onShare={
              allowSharingToHomeProject ? handleSharingToHomeProject : undefined
            }
          />
        )}

        <FileDeleteModal
          fileId={fileToBeDeleted}
          loading={loading}
          loadingFileId={loadingFileId}
          onClose={() => setFileToBeDeleted(undefined)}
          onConfirm={() => onDelete(fileToBeDeleted!)}
        />
        <DownloadAllModal
          visible={confirmDownloadAll}
          loading={loading}
          onClose={() => setConfirmDownloadAll(false)}
          onConfirm={handleDownloadAll}
        />
      </SectionContainer>
    </PaddedContainer>
  );
};
